import React from 'react';
import Spinner from "react-loading-indicators/OrbitProgress";

/**
 * Hydrates and displays search results with pagination.
 * @param {Object} props
 * @param {Array} props.results - Array of search result objects.
 * @param {boolean} props.loading - Loading state.
 * @param {string|null} props.error - Error message, if any.
 * @param {Function} props.onSelectResult - Callback when a result is selected.
 * @param {number} props.currentPage - Current page (0-based).
 * @param {number} props.totalPages - Total number of pages.
 * @param {Function} props.onChangePage - Callback when a page is selected.
 */
const SearchResults = ({ results, loading, error, onSelectResult, currentPage = 0, totalPages = 1, onChangePage }) => {
  // Helper to render pagination numbers with ellipsis
  const renderPagination = () => {
    if (totalPages <= 1) return null;
    const pages = [];
    const maxPages = 10;
    let start = 0;
    let end = totalPages;
    if (totalPages > maxPages) {
      if (currentPage < 5) {
        start = 0;
        end = maxPages - 2;
        pages.push(...Array.from({ length: end }, (_, i) => i));
        pages.push('ellipsis');
        pages.push(totalPages - 1);
      } else if (currentPage > totalPages - 6) {
        pages.push(0);
        pages.push('ellipsis');
        start = totalPages - (maxPages - 2);
        end = totalPages;
        pages.push(...Array.from({ length: end - start }, (_, i) => start + i));
      } else {
        pages.push(0);
        pages.push('ellipsis');
        start = currentPage - 2;
        end = currentPage + 3;
        pages.push(...Array.from({ length: end - start }, (_, i) => start + i));
        pages.push('ellipsis');
        pages.push(totalPages - 1);
      }
    } else {
      pages.push(...Array.from({ length: totalPages }, (_, i) => i));
    }
    return (
      <nav className="flex justify-center mt-6" aria-label="Pagination">
        <ul className="inline-flex items-center gap-1" role="list">
          <li>
            <button
              className={`px-3 py-1 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 text-sm font-medium transition-colors
                ${currentPage === 0 ? 'text-gray-500 cursor-default bg-transparent' : 'text-blue-700 hover:underline bg-transparent'}`}
              aria-label="Previous page"
              onClick={() => onChangePage && onChangePage(currentPage - 1)}
              disabled={currentPage === 0}
              tabIndex={0}
              style={{ background: 'transparent', boxShadow: 'none' }}
            >
              &laquo; Prev
            </button>
          </li>
          {pages.map((p, idx) =>
            p === 'ellipsis' ? (
              <li key={`ellipsis-${idx}`} aria-hidden="true" className="px-2 text-gray-400 select-none">…</li>
            ) : (
              <li key={p}>
                {p === currentPage ? (
                  <span
                    className="px-3 py-1 rounded text-sm font-medium text-gray-600 cursor-default bg-transparent"
                    aria-current="page"
                    style={{ background: 'transparent', boxShadow: 'none' }}
                  >
                    {p + 1}
                  </span>
                ) : (
                  <button
                    className="px-3 py-1 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 text-sm font-medium text-blue-700 hover:underline bg-transparent transition-colors"
                    aria-label={`Go to page ${p + 1}`}
                    onClick={() => onChangePage && onChangePage(p)}
                    tabIndex={0}
                    style={{ background: 'transparent', boxShadow: 'none' }}
                  >
                    {p + 1}
                  </button>
                )}
              </li>
            )
          )}
          <li>
            <button
              className={`px-3 py-1 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 text-sm font-medium transition-colors
                ${currentPage === totalPages - 1 ? 'text-gray-500 cursor-default bg-transparent' : 'text-blue-700 hover:underline bg-transparent'}`}
              aria-label="Next page"
              onClick={() => onChangePage && onChangePage(currentPage + 1)}
              disabled={currentPage === totalPages - 1}
              tabIndex={0}
              style={{ background: 'transparent', boxShadow: 'none' }}
            >
              Next &raquo;
            </button>
          </li>
        </ul>
      </nav>
    );
  };


  if (loading) return (
    <div className="flex justify-center items-start mt-[85px] min-h-[120px]">
      <Spinner dense color="#8034c4" size="large" speedPlus="-1" text="Loading" textColor="" aria-busy="true" aria-live="polite" role="status" aria-label="Loading" />
    </div>
  );

  // if (loading) {
  //   return (
  //     <div className="space-y-3">
  //       {Array.from({ length: 5 }).map((_, i) => (
  //         <div key={i} className="animate-pulse bg-muted rounded-lg p-4 h-20" />
  //       ))}
  //     </div>
  //   );
  // }
  if (error) {
    return <div className="text-red-600 py-4">{error}</div>;
  }
  if (!results || results.length === 0) {
    return (
      <div className="text-center py-8 bg-muted/30 rounded-lg">
        <h2 className="text-lg md:text-xl font-medium mb-2">No results found</h2>
        <p className="text-sm md:text-base text-muted-foreground">
          Try adjusting your search terms or browse our modules instead.
        </p>
      </div>
    );
  }
  return (
    <div className="space-y-3">
      {results.map((result) => (
        <button
          key={result.id}
          type="button"
          tabIndex={0}
          className="block w-full text-left rounded-xl border border-gray-200 bg-white transition-colors outline-none focus:ring-2 focus:ring-primary/60 focus:border-primary/60 hover:border-primary hover:bg-primary/10 cursor-pointer p-4"
          style={{ textDecoration: 'none' }}
          onClick={() => onSelectResult && onSelectResult(result.id)}
        >
          <div className="flex items-center gap-2 mb-1">
            <svg className="h-4 w-4 text-primary" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
            <span className="text-base md:text-lg font-semibold">{result.title}</span>
          </div>
          <div className="text-xs md:text-sm text-muted-foreground mb-1">
            In: {result.groupTitle} &gt; {result.topicTitle} &gt; {result.title}
          </div>
        </button>
      ))}
      {renderPagination()}
    </div>
  );
};

export default SearchResults;
